import { matchingItems, VENUE_SEARCH_THRESHOLD } from './selector-search';
import { useRefreshAvailableApps, type AvailableAppItem } from './use-available-apps';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, TextField, Typography, roleLabel } from '@idu-nightlife/ui-kit';
import { Box, ListSubheader, Menu, MenuItem } from '@mui/material';
import { useState, type KeyboardEvent } from 'react';

const MENU_MAX_HEIGHT = 'min(480px, calc(100% - 96px))';

// Keys the menu itself needs from the field: moving into the list and closing.
const MENU_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Escape', 'Tab']);

const itemKey = (item: AvailableAppItem) => `${item.application}:${item.venue?.id ?? 'root'}`;

export function ApplicationRoleSelector(
  props: Readonly<{
    applicationRoles: AvailableAppItem[];
    onSelect: (item: AvailableAppItem) => void;
    selected: AvailableAppItem | undefined;
  }>,
) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const refreshAvailableApps = useRefreshAvailableApps();

  const open = Boolean(anchorEl);

  const handleOpen = (element: HTMLElement) => {
    refreshAvailableApps();
    setAnchorEl(element);
  };

  const onSelect = (e: AvailableAppItem) => {
    props.onSelect(e);
    setAnchorEl(null);
  };

  return (
    <>
      <CurrentSelectedButton selected={props.selected} setAnchorEl={handleOpen} />
      <ApplicationRoleMenu
        open={open}
        setAnchorEl={setAnchorEl}
        onSelect={onSelect}
        anchorEl={anchorEl}
        applicationRoles={props.applicationRoles}
        selected={props.selected}
      />
    </>
  );
}

function ApplicationRoleMenu(
  props: Readonly<{
    open: boolean;
    anchorEl: HTMLElement | null;
    setAnchorEl: (e: HTMLElement | null) => void;
    applicationRoles: AvailableAppItem[];
    onSelect: (item: AvailableAppItem) => void;
    selected: AvailableAppItem | undefined;
  }>,
) {
  const [query, setQuery] = useState('');
  const searchable = props.applicationRoles.length > VENUE_SEARCH_THRESHOLD;
  const items = searchable ? matchingItems(props.applicationRoles, query) : props.applicationRoles;

  const close = () => {
    setQuery('');
    props.setAnchorEl(null);
  };

  const select = (item: AvailableAppItem) => {
    setQuery('');
    props.onSelect(item);
  };

  const onSearchKeyDown = (event: KeyboardEvent) => {
    if (MENU_KEYS.has(event.key)) return;
    event.stopPropagation();

    const [topMatch] = items;
    if (event.key === 'Enter' && topMatch) {
      event.preventDefault();
      select(topMatch);
    }
  };

  return (
    <Menu
      anchorEl={props.anchorEl}
      open={props.open}
      onClose={close}
      autoFocus={!searchable}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      transitionDuration={200}
      slotProps={{ paper: { sx: { maxHeight: MENU_MAX_HEIGHT } } }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'grey.900' } }}
    >
      {searchable && (
        <ListSubheader sx={{ py: 1, lineHeight: 'normal' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search venues"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            slotProps={{ htmlInput: { 'aria-label': 'Search venues' } }}
          />
        </ListSubheader>
      )}

      {searchable && items.length === 0 && (
        <MenuItem disabled sx={{ justifyContent: 'center', py: 1.5 }}>
          <Typography.Body>No venues match “{query.trim()}”</Typography.Body>
        </MenuItem>
      )}

      {items.map((item, index) => {
        const isSelected =
          props.selected?.application === item.application &&
          props.selected?.venue?.id === item.venue?.id &&
          props.selected?.role === item.role;

        return (
          <MenuItem
            key={itemKey(item)}
            onClick={() => select(item)}
            sx={{
              display: 'flex',
              gap: 1.5,
              py: 1.5,

              justifyContent: 'center',
              borderTop: index === 0 ? `1px solid` : 'none',
              borderBottom: `1px solid`,
              borderColor: 'divider',
              backgroundColor: isSelected ? 'neutral.light' : 'transparent',
            }}
          >
            <Box sx={{ display: 'flex' }}>
              {item.logo && (
                <item.logo width={80} height={16} preserveAspectRatio="xMinYMid meet" />
              )}
            </Box>
            <SelectedLabel item={item} />
          </MenuItem>
        );
      })}
    </Menu>
  );
}

function CurrentSelectedButton(
  props: Readonly<{ selected?: AvailableAppItem; setAnchorEl: (element: HTMLElement) => void }>,
) {
  return (
    <Button
      variant="custom"
      data-testid="global-nav-selector"
      sx={{
        gap: 1,
        color: 'grey.50',
        justifyContent: 'flex-start',
        '&:hover': { color: 'grey.200' },
      }}
      onClick={(e) => props.setAnchorEl(e.currentTarget)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SelectedLabel item={props.selected} />
      </Box>
      <ExpandMoreIcon fontSize="small" />
    </Button>
  );
}

function SelectedLabel({ item }: Readonly<{ item?: AvailableAppItem }>) {
  if (!item) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography.Body>Select</Typography.Body>
      </Box>
    );
  }

  return item.venue ? (
    <Typography.Title sx={{ flex: 1, lineClamp: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {item.venue.name} - {roleLabel(item)}
    </Typography.Title>
  ) : null;
}
